import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  AutoAwesome as AutoAwesomeIcon,
  PlaylistAddCheck as ManualReviewIcon,
  ListAlt as TransactionsIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import axios from 'axios';

// Sub-components
import TrustIndicators from './reconciliation/TrustIndicators';
import ProcessingStatus from './reconciliation/ProcessingStatus';
import JournalEntriesList from './reconciliation/JournalEntriesList';
import GLAccountsSummary from './reconciliation/GLAccountsSummary';
import ClassificationStats from './reconciliation/ClassificationStats';
import ClassificationQueue from './reconciliation/ClassificationQueue';
import FuzzyMatchReview from './reconciliation/FuzzyMatchReview';
import TransactionsBrowser from './reconciliation/TransactionsBrowser';
import CounterpartyLedger from './reconciliation/CounterpartyLedger';
import WarehouseAP from './reconciliation/WarehouseAP';
import ApMatchReview from './reconciliation/ApMatchReview';
import { APReportsManager } from './procurement';
import { resolveTabIndex } from './reconciliationTabs';
import BankFeedsRefresh from './BankFeedsRefresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ReconciliationHub() {
  // ?tab=<name> deep link. The name->index map lives in reconciliationTabs.js and is
  // asserted against the rendered strip by reconciliationTabs.test.js — the previous
  // hardcoded literal had already gone stale once during a tab renumbering, which
  // silently sends every saved link to the wrong screen.
  const initialTab = typeof window === 'undefined'
    ? 0
    : resolveTabIndex(new URLSearchParams(window.location.search).get('tab'));
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data
  const [reconciliationStatus, setReconciliationStatus] = useState(null);
  const [journalSummary, setJournalSummary] = useState(null);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch main dashboard metrics
      const [statusRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/accounting/reconciliation-status`, config),
        axios.get(`${API_URL}/api/accounting/journal-entries-summary`, config)
      ]);

      if (statusRes.data.success) {
        setReconciliationStatus(statusRes.data.data);
      }

      if (summaryRes.data.success) {
        setJournalSummary(summaryRes.data.data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load reconciliation data');
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="600">
            Finance Reconciliation Hub
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive view of journal entries, balances, and reconciliation status
          </Typography>
        </Box>
        {/* Discreet entry point to Financial Statements. That page is intentionally kept
            out of the sidebar (its figures are not yet fully accurate — see navigation.js);
            this quiet link is the only surfaced way in. */}
        <Link
          component={RouterLink}
          to="/finance/statements"
          variant="caption"
          color="text.secondary"
          underline="hover"
          sx={{ whiteSpace: 'nowrap', mt: 1, flexShrink: 0 }}
        >
          Financial Statements →
        </Link>
      </Box>

      {/* Bank feed freshness + one-click refresh-and-book */}
      <BankFeedsRefresh />

      {/* Trust Indicators - Always visible at top (CFO Priority) */}
      <Box sx={{ mb: 4 }}>
        <TrustIndicators data={reconciliationStatus} onRefresh={fetchDashboardData} />
      </Box>

      {/* Processing/backlog telemetry moved into the Backlog Analysis tab
          (2026-07-11, CFO audit loop): "20.5% Complete" on the landing view read
          as "the ledger is 4/5ths missing" when the books are balanced and the
          pending mass is 2017-2020 historical rows awaiting an era policy. */}

      {/* Tabbed Navigation for Different Personas */}
      <Paper sx={{ width: '100%' }}>
        {/* 2026-07-10 tab rationalization (CFO audit loop): removed three tabs —
            Connector Mappings (duplicate of the Bank GL Mappings screen, same
            connector_gl_mappings table), Supplier Invoices (all 1,481 invoices are
            status=approved; the approval queue never has content — invoices arrive
            approved via Warehouse AP), Payment Schedule (invoice_payment_schedule
            holds 2 rows; payment planning ships in the Cash Flow drawer instead).
            Scrollable variant so the remaining labels never truncate. */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: '64px',
              textTransform: 'none',
              fontSize: '0.95rem'
            }
          }}
        >
          <Tab
            icon={<ReceiptIcon />}
            iconPosition="start"
            label="Journal Entries"
          />
          <Tab
            icon={<AccountBalanceIcon />}
            iconPosition="start"
            label="Chart of Accounts"
          />
          <Tab
            icon={<AutoAwesomeIcon />}
            iconPosition="start"
            label="AI Classifications"
          />
          <Tab
            icon={<AccountBalanceIcon />}
            iconPosition="start"
            label="AP Reports"
          />
          <Tab
            icon={<ManualReviewIcon />}
            iconPosition="start"
            label="Fuzzy Match Review"
          />
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="Backlog Analysis"
          />
          <Tab
            icon={<TransactionsIcon />}
            iconPosition="start"
            label="All Transactions"
          />
          <Tab
            icon={<AccountBalanceIcon />}
            iconPosition="start"
            label="Counterparties"
          />
          <Tab
            icon={<LocalShippingIcon />}
            iconPosition="start"
            label="Warehouse AP"
          />
          <Tab
            icon={<AccountBalanceIcon />}
            iconPosition="start"
            label="AP Match Review"
          />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <JournalEntriesList summary={journalSummary} />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <GLAccountsSummary />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box>
            <ClassificationStats />
            <ClassificationQueue />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <APReportsManager />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <FuzzyMatchReview />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Box sx={{ mb: 4 }}>
            <ProcessingStatus
              data={reconciliationStatus}
              onRefresh={fetchDashboardData}
            />
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Backlog Breakdown by Source
            </Typography>
            {reconciliationStatus?.backlog_by_source && (
              <Box sx={{ mt: 2 }}>
                {reconciliationStatus.backlog_by_source.map((source, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {source.icon} {source.display_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Oldest: {source.oldest_date}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="h6" fontWeight="600">
                          {source.count.toLocaleString()}
                        </Typography>
                        {Array.isArray(source.amounts_by_currency) && source.amounts_by_currency.length > 1 ? (
                          source.amounts_by_currency.map((a) => (
                            <Typography key={a.currency} variant="body2" color="text.secondary">
                              {a.currency} {parseFloat(a.amount).toLocaleString()}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {source.currency} {parseFloat(source.amount).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => {
                          setActiveTab(2);
                        }}
                      >
                        Run AI Classification
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ManualReviewIcon />}
                        onClick={() => {
                          setActiveTab(2);
                        }}
                      >
                        Manual Review
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <TransactionsBrowser />
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          <CounterpartyLedger />
        </TabPanel>

        <TabPanel value={activeTab} index={8}>
          <WarehouseAP />
        </TabPanel>

        <TabPanel value={activeTab} index={9}>
          <ApMatchReview />
        </TabPanel>
      </Paper>
    </Container>
  );
}
